import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import type { Meghivo } from '../kollegak/meghivoAllapot.ts';
import KiosztasOlvaso from '../kollegak/KiosztasOlvaso.tsx';
import MeghivoUrlap from '../kollegak/MeghivoUrlap.tsx';
import TagSor, { type Tag } from '../kollegak/TagSor.tsx';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Fokonyv, HibaUzenet, OldalFej, Ures } from '../ui/elemek.tsx';
import NincsOldal from './NincsOldal.tsx';

export default function Kollegak() {
  const { jogok, aktivId } = useAktivIroda();
  const { session } = useAuth();
  const tagok = useQuery({
    queryKey: ['kollegak', aktivId],
    enabled: jogok.vezeto && aktivId !== null,
    queryFn: async () => {
      const { data, error } = await supabase.from('office_members')
        .select('user_id, szerep, aktiv, szabadsagon, tipus_szuro, utolso_kiosztas_at, profil:profiles(nev, email, telefon)')
        .eq('office_id', aktivId!);
      if (error) throw error;
      return (data as unknown as Tag[]).sort((a, b) =>
        Number(b.aktiv) - Number(a.aktiv) || (a.profil?.nev ?? '').localeCompare(b.profil?.nev ?? '', 'hu'));
    },
  });
  // A még el nem fogadott meghívók (az auth.users-ből, ezért külön függvény).
  const meghivok = useQuery({
    queryKey: ['kollegak-meghivok', aktivId],
    enabled: jogok.vezeto && aktivId !== null,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kollegak_meghivo_allapot', { p_office_id: aktivId! });
      if (error) throw error;
      return new Map<string, Meghivo>(data.map((m) => [m.user_id, m]));
    },
  });
  // Ki kész tag (belépett, van TOTP-ja): csak ő kaphat leadet (az auth.mfa_factors-ból, ezért külön függvény).
  const keszTagok = useQuery({
    queryKey: ['kollegak-kesz', aktivId],
    enabled: jogok.vezeto && aktivId !== null,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kollegak_kesz', { p_office_id: aktivId! });
      if (error) throw error;
      return new Map<string, boolean>(data.map((m) => [m.user_id, m.kesz]));
    },
  });

  if (!jogok.vezeto) return <NincsOldal />;
  if (!aktivId) return <Ures cim="Nincs kiválasztott iroda" />;
  return (
    <div className="grid gap-5">
      <OldalFej cim="Kollégák" leiras="Az iroda tagjai, a jogosultságaik és az, hogy milyen leadet kaphatnak.">
        <MeghivoUrlap officeId={aktivId} />
      </OldalFej>
      <KiosztasOlvaso officeId={aktivId} />
      <HibaUzenet hiba={tagok.error ?? meghivok.error ?? keszTagok.error} />
      {tagok.isPending && <Betolto />}
      {tagok.data?.length === 0 && <Ures cim="Még nincs kolléga" szoveg="Hívd meg az első munkatársat a fenti gombbal." />}
      {tagok.data && tagok.data.length > 0 && (
        <Fokonyv>
          {tagok.data.map((t) => <TagSor key={t.user_id} tag={t} officeId={aktivId} sajat={t.user_id === session?.user.id}
            meghivo={meghivok.data?.get(t.user_id) ?? null} kesz={keszTagok.data?.get(t.user_id)} />)}
        </Fokonyv>
      )}
    </div>
  );
}
